import React, { useState, useEffect } from 'react';
import './NotePickerMenu.css';

export interface NotePickerMenuProps {
  /** Current note value (e.g., "C4") */
  currentNote: string;
  /** Track index for display */
  trackIndex: number;
  /** Step index for display */
  stepIndex: number;
  /** Callback when note is selected */
  onNoteSelect: (note: string) => void;
  /** Callback when menu is closed */
  onClose: () => void;
}

const NOTE_LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const OCTAVES = [1, 2, 3, 4, 5, 6, 7];

/**
 * NotePickerMenu Component
 * 
 * Displays a scrollable menu for selecting notes with separate sections:
 * - Note letter (C, D, E, F, G, A, B)
 * - Sharp/Natural toggle
 * - Octave selection
 * 
 * Replaces the prompt dialog for better mobile UX
 */
export const NotePickerMenu: React.FC<NotePickerMenuProps> = ({
  currentNote,
  trackIndex,
  stepIndex,
  onNoteSelect,
  onClose,
}) => {
  // Parse current note
  const parseNote = (note: string): { letter: string; sharp: boolean; octave: number } => {
    const match = note.match(/^([A-G])(#?)(\d+)$/);
    if (!match) return { letter: 'C', sharp: false, octave: 4 };
    
    return {
      letter: match[1] || 'C',
      sharp: match[2] === '#',
      octave: parseInt(match[3] || '4', 10),
    };
  };

  const parsed = parseNote(currentNote);
  const [selectedLetter, setSelectedLetter] = useState(parsed.letter);
  const [isSharp, setIsSharp] = useState(parsed.sharp);
  const [selectedOctave, setSelectedOctave] = useState(parsed.octave);

  // Update preview when selections change
  const previewNote = `${selectedLetter}${isSharp ? '#' : ''}${selectedOctave}`;

  // Handle backdrop click to close
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle confirm
  const handleConfirm = () => {
    onNoteSelect(previewNote);
    onClose();
  };

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div 
      className="note-picker-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="note-picker-title"
    >
      <div className="note-picker-menu">
        {/* Header */}
        <div className="note-picker-header">
          <h3 id="note-picker-title">
            Select Note
          </h3>
          <p className="note-picker-subtitle">
            Track {trackIndex + 1}, Step {stepIndex + 1}
          </p>
        </div>

        {/* Preview */}
        <div className="note-picker-preview">
          <div className="note-picker-preview-label">Preview:</div>
          <div className="note-picker-preview-note">{previewNote}</div>
        </div>

        {/* Note Letter Section */}
        <div className="note-picker-section">
          <div className="note-picker-section-label">Note Letter</div>
          <div className="note-picker-options">
            {NOTE_LETTERS.map((letter) => (
              <button
                key={letter}
                className={`note-picker-option ${selectedLetter === letter ? 'selected' : ''}`}
                onClick={() => setSelectedLetter(letter)}
                aria-pressed={selectedLetter === letter}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Sharp/Natural Section */}
        <div className="note-picker-section">
          <div className="note-picker-section-label">Accidental</div>
          <div className="note-picker-options">
            <button
              className={`note-picker-option ${!isSharp ? 'selected' : ''}`}
              onClick={() => setIsSharp(false)}
              aria-pressed={!isSharp}
            >
              ♮ Natural
            </button>
            <button
              className={`note-picker-option ${isSharp ? 'selected' : ''}`}
              onClick={() => setIsSharp(true)}
              aria-pressed={isSharp}
            >
              ♯ Sharp
            </button>
          </div>
        </div>

        {/* Octave Section */}
        <div className="note-picker-section">
          <div className="note-picker-section-label">Octave</div>
          <div className="note-picker-options note-picker-octaves">
            {OCTAVES.map((octave) => (
              <button
                key={octave}
                className={`note-picker-option ${selectedOctave === octave ? 'selected' : ''}`}
                onClick={() => setSelectedOctave(octave)}
                aria-pressed={selectedOctave === octave}
              >
                {octave}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="note-picker-actions">
          <button
            className="note-picker-button note-picker-button-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="note-picker-button note-picker-button-confirm"
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};
